import React, { useState, useEffect } from 'react';
import * as Realm from 'realm-web';
import colors from './config/colors';

const EditEmployeeDetails = ({ employee, onBack, user }) => {
  const [firstName, setFirstName] = useState(employee?.firstName || '');
  const [lastName, setLastName] = useState(employee?.lastName || '');
  const [jobTitle, setJobTitle] = useState(employee?.jobTitle || '');
  const [employeeId, setEmployeeId] = useState(employee?.employeeId || '');
  const [isAdmin, setIsAdmin] = useState(employee?.isAdmin || false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(employee?.isSuperAdmin || false);
  const [isBRAdmin, setIsBRAdmin] = useState(employee?.isBRAdmin || false);
  const [isPasswordChange, setIsPasswordChange] = useState(false);
  const [isUsernameChange, setIsUsernameChange] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [confirmNewUsername, setConfirmNewUsername] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [arbeitszeitmodell, setArbeitszeitmodell] = useState(employee.timeModel || 'Vollzeit');
  const [hoursMonth, setHoursMonth] = useState(employee?.hours_month && typeof employee.hours_month === 'string' ? employee.hours_month : '0');
  const [hoursWeek, setHoursWeek] = useState(employee?.hours_week && typeof employee.hours_week === 'string' ? employee.hours_week : '0');
  const [holidayValue, setHolidayValue] = useState(employee?.holidayValue && typeof employee.holidayValue === 'string' ? employee.holidayValue : '0');
  const [isTrustedEmployee, setIsTrustedEmployee] = useState(employee?.isTrustedEmployee || false);
  const [contractualAverageWorkingTime, setContractualAverageWorkingTime] = useState(employee?.contractualAverageWorkingTime || 0);
  const [contractModel, setContractModel] = useState(employee?.contractModel || '');
  const [contractStartDate, setContractStartDate] = useState(() => {
    if (!employee?.contractStartDate) return '';
    try {
      const date = new Date(employee.contractStartDate);
      return !isNaN(date.getTime()) ? date.toISOString().split('T')[0] : '';
    } catch (e) {
      return '';
    }
  });
  const [contractModels, setContractModels] = useState([]);
  const token = localStorage.getItem('accessToken');
  const [currentUser, setCurrentUser] = useState(null);

  const app = new Realm.App({ id: process.env.REACT_APP_MONGO_APP });

  useEffect(() => {
    fetch('https://eu-central-1.aws.data.mongodb-api.com/app/data-ywwpoom/endpoint/getContractModels')
      .then(response => response.json())
      .then(data => {
        if (!data.error) {
          setContractModels(data);
        }
      })
      .catch(error => console.error('Error fetching contract models:', error));
  }, []);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_EMPLOYEE_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Access-Control-Request-Headers': '*',
          },
          body: JSON.stringify({ token }),
          redirect: 'follow'
        });
        const result = await response.json();
        if (!result.error) {
          setCurrentUser(result);
        }
      } catch (error) {
        console.error("Error fetching current user:", error);
      }
    };
    
    fetchCurrentUser();
  }, [token]);

  const handleAdminCheckboxChange = () => {
    setIsAdmin(true);
    setIsSuperAdmin(false);
    setIsBRAdmin(false);
  };

  const handleSuperAdminCheckboxChange = () => {
    setIsAdmin(true);
    setIsSuperAdmin(true);
    setIsBRAdmin(false);
  };

  const handleBRadminCheckboxChange = () => {
    setIsAdmin(false);
    setIsSuperAdmin(false);
    setIsBRAdmin(true);
  };

  const handleNoAdminCheckboxChange = () => {
    setIsAdmin(false);
    setIsSuperAdmin(false);
    setIsBRAdmin(false);
  };

  const handleTrustedEmployeeChange = (value) => {
    setIsTrustedEmployee(value);
  };

  const handleSave = async () => {
    const url = 'https://eu-central-1.aws.data.mongodb-api.com/app/data-ywwpoom/endpoint/changeEmployee';

    const requestBody = {
      token,
      email: employee.email,
      firstName,
      lastName,
      jobTitle,
      employeeId,
      isAdmin,
      isSuperAdmin,
      isBRAdmin,
      arbeitszeitmodell,
      hoursMonth,
      hoursWeek,
      holidayValue,
      isTrustedEmployee,
      contractualAverageWorkingTime: Number(contractualAverageWorkingTime),
      contractModel,
      contractStartDate: new Date(contractStartDate),
      logUser: currentUser ? {
        employeeId: currentUser.employeeId,
        email: currentUser.email || ''
      } : null
    };

    if (isUsernameChange && newUsername === confirmNewUsername) {
      requestBody.newEmail = newUsername;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.success) {
        const updatedEmployee = { 
          ...employee, 
          firstName, 
          lastName, 
          jobTitle, 
          employeeId, 
          isAdmin, 
          isSuperAdmin,
          isBRAdmin,
          timeModel: arbeitszeitmodell,
          hours_month: hoursMonth,
          hours_week: hoursWeek,
          email: newUsername || employee.email,
          holidayValue,
          isTrustedEmployee,
          contractualAverageWorkingTime: Number(contractualAverageWorkingTime),
          contractModel,
          contractStartDate: new Date(contractStartDate)
        };
        onBack(updatedEmployee);
      } else {
        setErrorMessage(result.error || 'Failed to update employee details');
      }
    } catch (error) {
      setErrorMessage('An error occurred while updating employee details');
    }
  };

  const handleDelete = async () => {
    const url = 'https://eu-central-1.aws.data.mongodb-api.com/app/data-ywwpoom/endpoint/deleteEmployee';

    const requestBody = {
      token,
      email: employee.email,
      logUser: currentUser ? {
        employeeId: currentUser.employeeId,
        email: currentUser.email || ''
      } : null
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.success) {
        onBack(null, true);
      } else {
        setErrorMessage(result.error || 'Failed to delete employee');
      }
    } catch (error) {
      setErrorMessage('An error occurred while deleting employee');
    }
  };

  return (
    <div>
      <header className="flex justify-between items-center mb-[32px]">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Mitarbeiter bearbeiten</h1>
          <p className="text-sm text-gray-600">{employee.firstName} {employee.lastName} &gt; Bearbeiten</p>
        </div>
        {user?.isSuperAdmin && (
          <button 
            onClick={handleDelete} 
            className="bg-primaryLight text-textLight py-2 px-4 rounded-md hover:bg-primaryLightHover transition-colors"
          >
            Mitarbeiter entfernen
          </button>
        )}
      </header>

      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-4">Persönliche Informationen</h2>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 mb-1">Vorname</label>
            <input 
              className="bg-white p-2 rounded-md w-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
              value={firstName} 
              onChange={(e) => setFirstName(e.target.value)} 
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-1">Nachname</label>
            <input 
              className="bg-white p-2 rounded-md w-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
              value={lastName} 
              onChange={(e) => setLastName(e.target.value)} 
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-1">Jobtitel</label>
            <input 
              className="bg-white p-2 rounded-md w-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
              value={jobTitle} 
              onChange={(e) => setJobTitle(e.target.value)} 
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-1">Mitarbeiter-ID</label>
            <input 
              className="bg-white p-2 rounded-md w-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
              value={employeeId} 
              onChange={(e) => setEmployeeId(e.target.value)} 
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-1">Monatsarbeitsstunden</label>
            <input 
              className="bg-white p-2 rounded-md w-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
              defaultValue={hoursMonth == typeof String ? employee?.hours_month : '0'}
              value={hoursMonth} 
              onChange={(e) => setHoursMonth(e.target.value)} 
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-1">Wochenarbeitsstunden</label>
            <input 
              className="bg-white p-2 rounded-md w-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
              value={hoursWeek} 
              onChange={(e) => setHoursWeek(e.target.value)} 
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-1">Urlaubstage</label>
            <input 
              className="bg-white p-2 rounded-md w-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
              value={holidayValue} 
              onChange={(e) => setHolidayValue(e.target.value)} 
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-1">Vertraglich geschuldete durchschnittliche Arbeitszeit</label>
            <input 
              type="number"
              className="bg-white p-2 rounded-md w-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
              value={contractualAverageWorkingTime} 
              onChange={(e) => setContractualAverageWorkingTime(e.target.value)} 
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-1">Vertragsmodell</label>
            <select 
              className="bg-white p-2 rounded-md w-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
              value={contractModel} 
              onChange={(e) => setContractModel(e.target.value)}
            >
              <option value="">Vertragsmodell auswählen</option>
              {contractModels.map(model => (
                <option key={model._id} value={model.name}>
                  {model.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-gray-700 mb-1">Startdatum, aktueller Arbeitsvertrag</label>
            <input 
              type="date"
              className="bg-white p-2 rounded-md w-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" 
              value={contractStartDate} 
              onChange={(e) => setContractStartDate(e.target.value)} 
            />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Karenzzeit</h2>
            <div className="flex items-center mb-4">
              <input 
                type="radio" 
                name="karrenzeit" 
                value="no" 
                className="mr-2 accent-primary" 
                checked={!isTrustedEmployee}
                onChange={() => handleTrustedEmployeeChange(false)}
              />
              <label className="text-gray-700">Ja</label>
            </div>
            <div className="flex items-center mb-4">
              <input 
                type="radio" 
                name="karrenzeit" 
                value="yes" 
                className="mr-2 accent-primary" 
                checked={isTrustedEmployee}
                onChange={() => handleTrustedEmployeeChange(true)}
              />
              <label className="text-gray-700">Nein</label>
            </div>
          </div>
        </div>

        {
          user?.isSuperAdmin &&
          <div className="mt-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Adminrechte bearbeiten?</h2>
            <div className="flex items-center mb-4">
              <input 
                type="radio" 
                name="adminRights" 
                value="admin" 
                className="mr-2 accent-primary" 
                checked={isAdmin && !isSuperAdmin && !isBRAdmin}
                onChange={handleAdminCheckboxChange}
              />
              <label className="text-gray-700">Ja, als Admin anlegen</label>
            </div>
            <div className="flex items-center mb-4">
              <input 
                type="radio" 
                name="adminRights" 
                value="superAdmin" 
                className="mr-2 accent-primary" 
                checked={isSuperAdmin && !isBRAdmin}
                onChange={handleSuperAdminCheckboxChange}
              />
              <label className="text-gray-700">Ja, als Super Admin anlegen</label>
            </div>
            <div className="flex items-center mb-4">
              <input 
                type="radio" 
                name="adminRights" 
                value="BRadmin" 
                className="mr-2 accent-primary" 
                checked={isBRAdmin}
                onChange={handleBRadminCheckboxChange}
              />
              <label className="text-gray-700">Ja, als Betriebsrat anlegen</label>
            </div>
            <div className="flex items-center mb-4">
              <input 
                type="radio" 
                name="adminRights" 
                value="none" 
                className="mr-2 accent-primary" 
                checked={!isAdmin && !isSuperAdmin && !isBRAdmin}
                onChange={handleNoAdminCheckboxChange}
              />
              <label className="text-gray-700">Nein, nicht als Admin anlegen</label>
            </div>
          </div>
        }

        {errorMessage && (
          <div className="bg-red-100 text-error p-4 rounded-md mb-4">
            {errorMessage}
          </div>
        )}

        <button 
          onClick={async () => {
            await app.emailPasswordAuth.sendResetPasswordEmail({email: employee.email})
          }} 
          className="bg-primary text-textLight py-2 px-4 rounded-md mt-[35px] font-[500] hover:bg-primaryDark transition-colors"
        >
          Email zum Passwort Zurücksetzen an den Mitarbeiter senden
        </button>

        <div className="flex mt-[35px]">
          <button 
            onClick={handleSave} 
            className="bg-primary text-textLight py-2 px-4 rounded-md mr-[12px] font-[500] hover:bg-primaryDark transition-colors"
          >
            Speichern
          </button>
          <button 
            onClick={() => onBack(employee)} 
            className="text-primary py-2 px-4 rounded-md font-[500] hover:underline"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditEmployeeDetails;